import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import type {
  CultureFilter,
  CulturesPage_CulturePlate,
  CulturesPageRequest,
  ObservationMetric,
  PlateFilter,
} from '~/api/backend/pages/culturesPageAPI'
import { QUERY_KEYS } from '~/api/query-client-keys'
import Breadcrumbs from '~/components/Breadcrumbs'
import { FullPageError } from '~/components/Errors'
import Input from '~/components/Input'
import LoadingMessage from '~/components/LoadingMessage'
import PopupMenu from '~/components/PopupMenu'
import { useFeatureFlag } from '~/core/featureFlags'
import { useDemoQuery } from '~/demoControls/DemoContext'
import { ExportCultureMetaDataDialog } from '~/pages/cultures/bulk-export/ExportCultureMetaDialog'
import { ExportPicklistDialog } from '~/pages/cultures/export-picklist/ExportPicklistDialog'
import { useDebounce } from '~/utils/hooks/useDebounce'
import CulturesPagePlateTable from './CulturesPagePlateTable'
import cs from './cultures_page.scss'
import FilterBar from './FilterBar/FilterBar'
import SelectionBar from './SelectionBar'

const _getCulturesPageRequest = (
  searchQuery: string,
  filters: PlateFilter[],
): CulturesPageRequest => {
  const plateFilters: PlateFilter[] = []

  if (searchQuery !== '') {
    plateFilters.push({
      field: 'plate_barcode',
      operator: {
        operator_type: 'contains_string',
        value: searchQuery,
      },
    })
  }

  if (filters.length > 0) {
    plateFilters.push(...filters)
  }

  return {
    plate_filters: plateFilters,
  }
}

export default function CulturesPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState<PlateFilter[]>([])
  const [selectedPlateIds, setSelectedPlateIds] = useState<Record<string, boolean>>({})
  const [isSelectionMode, setIsSelectionMode] = useState(false)
  const client = useBackendClient()

  const hackathon2025 = useFeatureFlag('hackathon2025') ?? false
  const requestedMetrics = useMemo<ObservationMetric[]>(
    () => (hackathon2025 ? ['cell_concentration'] : ['confluence']),
    [hackathon2025],
  )

  const demoData = useDemoQuery('monitor', 'CulturesPage')
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exportPicklistDialogOpen, setExportPicklistDialogOpen] = useState(false)

  // Debounce the search query and filters
  const debouncedSearchQuery = useDebounce(searchQuery, 250)
  const debouncedFilters = useDebounce(filters, 250)

  // Build the request params with debounced values
  const request = useMemo(
    () => _getCulturesPageRequest(debouncedSearchQuery, debouncedFilters),
    [debouncedSearchQuery, debouncedFilters],
  )

  // Use React Query's useInfiniteQuery for automatic pagination
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error: queryError,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.CULTURES_PAGE, request, requestedMetrics.join(',')],
    queryFn: async ({ pageParam }) => {
      return await client.culturesPage.getData(request, 50, pageParam, requestedMetrics)
    },
    getNextPageParam: lastPage => {
      return lastPage.hasMore ? lastPage.nextPageToken : undefined
    },
    enabled: demoData == null, // Skip query if demo data is available
  })

  // Flatten pages into single array
  const allPlates = useMemo(
    () => data?.pages.flatMap(page => page.filteredCulturePlates) ?? [],
    [data],
  )

  // Handle demo data
  let plates: CulturesPage_CulturePlate[]
  let error: Error | undefined
  let loading = isLoading
  let hasMore = false
  let isLoadingMore = false

  if (demoData != null) {
    const demoResponse = demoData(request, requestedMetrics)
    plates = demoResponse?.filteredCulturePlates ?? []
    error = undefined
    loading = false
    hasMore = false
    isLoadingMore = false
  } else {
    plates = allPlates
    error = queryError as Error | undefined
    hasMore = hasNextPage ?? false
    isLoadingMore = isFetchingNextPage
  }

  if (error) {
    return <FullPageError error={error} />
  }

  const selectedPlateCount = Object.keys(selectedPlateIds).length

  const handleToggleSelectionMode = () => {
    if (isSelectionMode) {
      // Exiting selection mode - clear selections
      setSelectedPlateIds({})
    }
    setIsSelectionMode(!isSelectionMode)
  }

  const handleExport = () => {
    setExportDialogOpen(true)
  }

  const handleExportPicklist = () => {
    setExportPicklistDialogOpen(true)
  }

  const options = [
    {
      label: 'Record Manual Passage',
      to: '/monitor/manual-passage-plates',
    },
  ]

  return (
    <div className={cs.culturesPage}>
      <div className={cs.topBar}>
        <div className={cs.controls}>
          <Breadcrumbs />
          {options.length > 0 && (
            <PopupMenu className={cs.menu} options={options} minimal />
          )}
          <div className={cs.spacer} />
          <Input
            className={cs.searchInput}
            inputClassName={cs.input}
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder='Search Plate Barcodes...'
          />
        </div>
      </div>

      {isSelectionMode && (
        <SelectionBar
          selectedCount={selectedPlateCount}
          totalPlatesCount={plates.length}
          onExport={handleExport}
          onExportPicklist={handleExportPicklist}
          onCancel={handleToggleSelectionMode}
        />
      )}

      {!isSelectionMode && (
        <FilterBar
          className={cs.filterBar}
          filters={filters}
          setFilters={setFilters}
          isSelectionMode={isSelectionMode}
          onToggleSelectionMode={handleToggleSelectionMode}
        />
      )}
      {loading && (
        <LoadingMessage className={cs.loadingMessage} label={'Loading cultures...'} />
      )}
      {!loading && (
        <Content
          plates={plates}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={fetchNextPage}
          selectedPlateIds={selectedPlateIds}
          onRowsSelected={setSelectedPlateIds}
          isSelectionMode={isSelectionMode}
        />
      )}

      {exportDialogOpen && (
        <ExportCultureMetaDataDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          initialFilters={[
            ...filters.filter(
              (filter): filter is CultureFilter => filter.field === 'culture_status',
            ),
          ]}
          selectedPlateBarcodes={plates
            .filter(plate => selectedPlateIds[plate.id])
            .map(plate => plate.barcode)}
        />
      )}

      {exportPicklistDialogOpen && (
        <ExportPicklistDialog
          open={exportPicklistDialogOpen}
          onClose={() => setExportPicklistDialogOpen(false)}
          initialFilters={[
            ...filters.filter(
              (filter): filter is CultureFilter => filter.field === 'culture_status',
            ),
          ]}
          selectedPlateBarcodes={plates
            .filter(plate => selectedPlateIds[plate.id])
            .map(plate => plate.barcode)}
        />
      )}
    </div>
  )
}

function Content(props: {
  plates: CulturesPage_CulturePlate[]
  hasMore: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
  selectedPlateIds: Record<string, boolean>
  onRowsSelected: (rowsId: Record<string, boolean>) => void
  isSelectionMode: boolean
}) {
  const {
    plates,
    hasMore,
    isLoadingMore,
    onLoadMore,
    selectedPlateIds,
    onRowsSelected,
    isSelectionMode,
  } = props

  const { ref, inView } = useInView({
    threshold: 0.1,
    rootMargin: '200px',
    triggerOnce: false,
  })

  // Load more when trigger comes into view
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore) {
      onLoadMore()
    }
  }, [inView, hasMore, isLoadingMore, onLoadMore])

  return (
    <div className={cs.content}>
      <div className={cs.tableWrapper}>
        <CulturesPagePlateTable
          plates={plates}
          isLoadingMore={isLoadingMore}
          selectableProps={
            isSelectionMode
              ? {
                  selectedRowIds: selectedPlateIds,
                  onRowsSelected: onRowsSelected,
                }
              : undefined
          }
        />
      </div>

      {/* Infinite scroll trigger */}
      {hasMore && (
        <div ref={ref} className={cs.loadMoreTrigger}>
          {isLoadingMore ? (
            <LoadingMessage label='Loading more plates...' />
          ) : (
            <div className={cs.scrollHint}>Scroll for more</div>
          )}
        </div>
      )}
    </div>
  )
}
